import { ReactNode, useState, useRef, useEffect, useCallback } from 'react'
import { useStore } from '../../lib/store'
import { LogOut, Settings, FileText, Users, Layers, Brain, ClipboardList, ChevronUp, Plus, Chrome } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChatWidget } from '../chat/ChatWidget'

interface Props {
  children: ReactNode
}

const NAV_ITEMS = [
  { path: '/', label: 'Visits', icon: FileText },
  { path: '/patients', label: 'Patients', icon: Users },
  { path: '/intake', label: 'Intake', icon: ClipboardList },
  { path: '/templates', label: 'Templates', icon: Layers },
  { path: '/learning', label: 'AI Learning', icon: Brain },
  { path: '/extension', label: 'Browser Extension', icon: Chrome },
  { path: '/settings', label: 'Settings', icon: Settings },
]

function initials(name?: string | null) {
  if (!name) return '?'
  return name
    .replace(/^Dr\.?\s+/i, '')
    .split(/\s+/)
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function UserMenu() {
  const { doctor, logout } = useStore()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Settings size={15} className="text-gray-400" />
            Account settings
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-t border-gray-100 dark:border-gray-800"
          >
            <LogOut size={15} className="text-gray-400" />
            Sign out
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
      >
        <div className="w-8 h-8 rounded-full bg-brand-500/15 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {initials(useStore.getState().doctor?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{doctor?.name}</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 truncate">{doctor?.specialty}</div>
        </div>
        <ChevronUp size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${open ? '' : 'rotate-180'}`} />
      </button>
    </div>
  )
}

// Idle auto-logout. Default 15 min of no input -> logout (PHI hygiene on shared
// computers). Configurable via VITE_IDLE_LOGOUT_MIN; 0 disables.
function useIdleLogout() {
  const { logout } = useStore()
  const timer = useRef<any>(null)
  const mins = Number((import.meta as any).env?.VITE_IDLE_LOGOUT_MIN ?? 15)

  const reset = useCallback(() => {
    if (!mins || mins <= 0) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { logout() }, mins * 60 * 1000)
  }, [mins, logout])

  useEffect(() => {
    if (!mins || mins <= 0) return
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange']
    const onActivity = () => {
      if (document.visibilityState === 'hidden') return
      reset()
    }
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    reset()
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity))
      if (timer.current) clearTimeout(timer.current)
    }
  }, [reset, mins])
}

export function AppShell({ children }: Props) {
  const { doctor } = useStore()
  const location = useLocation()
  const navigate = useNavigate()
  useIdleLogout()

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex flex-col w-60 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
        {/* Logo */}
        <div className="px-5 pt-5 pb-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-brand-600 dark:text-brand-400 tracking-tight">Dr. Scribey</span>
          </Link>
        </div>

        {/* Primary action */}
        <div className="px-3 pb-4">
          <button
            data-tour="new-visit"
            onClick={() => navigate('/new-encounter')}
            className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 active:bg-brand-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
          >
            <Plus size={16} strokeWidth={2.5} />
            New Visit
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon
            const active = item.path === '/'
              ? location.pathname === '/' || location.pathname.startsWith('/encounter')
              : location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                data-tour={item.path === '/' ? 'nav-visits' : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/60'
                }`}
              >
                <Icon size={17} strokeWidth={active ? 2.2 : 2} className={active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 dark:text-gray-500'} />
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* User block */}
        <div className="p-3 border-t border-gray-100 dark:border-gray-800">
          <UserMenu />
        </div>
      </aside>

      {/* ── Right side: mobile top bar + content ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-2.5 flex-shrink-0 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-brand-600 dark:text-brand-400">Dr. Scribey</span>
          </Link>
          <div className="flex items-center gap-1">
            <ChatWidget />
            <Link
              to="/settings"
              className="w-8 h-8 rounded-full bg-brand-500/15 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-semibold"
            >
              {initials(doctor?.name)}
            </Link>
          </div>
        </header>

        {/* Desktop chat: anchored bottom-right like a standard assistant launcher */}
        <div className="hidden md:block fixed bottom-6 right-6 z-30">
          <ChatWidget />
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6 pb-20 md:pb-6">
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-0 py-1 z-40 safe-area-inset-bottom">
          <div className="flex items-center justify-around">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              const active = item.path === '/'
                ? location.pathname === '/' || location.pathname.startsWith('/encounter')
                : location.pathname.startsWith(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-tour={item.path === '/' ? 'nav-visits' : undefined}
                  className={`flex flex-col items-center gap-0.5 py-1.5 px-0.5 flex-1 min-w-0 rounded-lg text-xs font-medium transition-colors ${
                    active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <Icon size={20} strokeWidth={active ? 2.2 : 2} />
                  <span className="text-[10px] whitespace-nowrap">{item.label === 'AI Learning' ? 'Learning' : item.label === 'Browser Extension' ? 'Extension' : item.label}</span>
                </Link>
              )
            })}
          </div>
        </nav>
      </div>
    </div>
  )
}
